import { Link } from "react-router-dom";
import { ArrowLeft, Shield } from "lucide-react";

const LAST_UPDATED = "June 4, 2026";

function Section({ id, title, children }) {
  return (
    <section id={id} className="mb-10" data-testid={`privacy-section-${id}`}>
      <h2 className="text-xl sm:text-2xl font-light text-white/90 mb-3">{title}</h2>
      <div className="space-y-3 text-white/55 text-sm sm:text-[15px] leading-7">{children}</div>
    </section>
  );
}

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[#050505]">
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center">
          <Link to="/" className="flex items-center gap-2 text-white/60 hover:text-white/90 transition-colors" data-testid="privacy-back-link">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm tracking-wider uppercase">Home</span>
          </Link>
        </div>
      </nav>

      <main className="pt-14 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center gap-3 mb-2">
          <Shield className="w-6 h-6 text-amber-400/80" />
          <h1 className="text-3xl sm:text-4xl font-light text-white/95" data-testid="privacy-title">Privacy Policy</h1>
        </div>
        <p className="text-white/35 text-sm mb-10">Last updated: {LAST_UPDATED}</p>

        <Section id="who-we-are" title="Who we are">
          <p>
            Daily Nakshatra ("Daily Nakshatra", "we", "us" or "our") operates the Daily Nakshatra website and
            mobile applications, which provide Vedic astrology content, nakshatra information, chart calculations
            and AI-assisted astrology reports (the "Service"). This policy explains what personal information we
            collect, why we collect it, who we share it with, and the choices and rights you have.
          </p>
          <p>
            If you have questions about this policy or your data, contact us at{" "}
            <a href="mailto:info@dailynakshatra.com" className="text-amber-400/80 hover:text-amber-400 underline">info@dailynakshatra.com</a>.
          </p>
        </Section>

        <Section id="information-we-collect" title="Information we collect">
          <p>We collect the following categories of information:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li><span className="text-white/75">Account details</span> — when you sign in with Google, we receive your name, email address and profile picture.</li>
            <li><span className="text-white/75">Birth information</span> — the birth date, time and place you enter to calculate charts. Birth place is converted to approximate coordinates and a time zone.</li>
            <li><span className="text-white/75">Saved charts</span> — your calculated natal chart and nakshatra placement, if you choose to save them.</li>
            <li><span className="text-white/75">Reports</span> — AI-generated reports you create (such as the Karmic Timeline), including the underlying chart facts and generation history.</li>
            <li><span className="text-white/75">Purchase records</span> — when you buy a report or subscribe, we store a transaction record (amount, item, date, status and the identifier returned by our payment processor). We do not store your full card number.</li>
            <li><span className="text-white/75">Usage data</span> — basic first-party analytics about how the Service is used (for example pages viewed and actions taken), plus technical data such as your device/browser type and approximate timing of requests.</li>
          </ul>
        </Section>

        <Section id="why-we-collect" title="Why we collect it">
          <ul className="list-disc pl-5 space-y-2">
            <li>To create and maintain your account and keep you signed in.</li>
            <li>To calculate charts and generate the reports and content you request.</li>
            <li>To process payments and manage subscriptions and report credits.</li>
            <li>To operate, secure, debug and improve the Service.</li>
            <li>To send the emails you have asked for (such as a weekly digest) and important service messages.</li>
          </ul>
          <p>
            We rely on your consent (for example, for optional emails and for generating reports from your birth
            data), on the performance of our agreement with you (to provide the Service you request), and on our
            legitimate interests (to keep the Service secure and working) as the legal bases for processing.
          </p>
        </Section>

        <Section id="where-stored" title="Where your data is stored">
          <p>
            Your data is stored in a managed MongoDB database hosted on our cloud infrastructure provider. Access is
            restricted and protected by authentication. Some information is held in short-lived caches to improve
            performance.
          </p>
        </Section>

        <Section id="third-parties" title="Services and third parties we use">
          <p>We share limited information only with the service providers needed to run the Service:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li><span className="text-white/75">Sign-in</span> — Google (via our managed authentication provider) for Google sign-in.</li>
            <li><span className="text-white/75">Hosting &amp; infrastructure</span> — our cloud platform provider, which hosts the application and database.</li>
            <li><span className="text-white/75">Payments</span> — Stripe processes payments and subscriptions. Stripe receives the information needed to complete your transaction and is responsible for your card data under its own privacy policy.</li>
            <li><span className="text-white/75">AI reports</span> — report text is generated using OpenAI models accessed through our managed AI provider. The chart facts and dates used to produce your report are sent for processing; we do not send your payment details.</li>
            <li><span className="text-white/75">Email</span> — we use an email delivery provider (Sender.net) to send digests and transactional email.</li>
            <li><span className="text-white/75">Analytics</span> — we use our own first-party analytics only. We do not use third-party advertising networks, ad tracking, or sell your data.</li>
          </ul>
        </Section>

        <Section id="cookies" title="Cookies, tracking and permissions">
          <p>
            We use a small number of cookies that are necessary for the Service to function: a secure, http-only
            session cookie to keep you signed in, and an anonymous identifier used to count report usage. We do not
            use advertising or cross-site tracking cookies.
          </p>
          <p>
            The Service does not require device permissions such as camera, microphone, contacts or precise GPS. Your
            birth place is entered manually and converted to approximate coordinates — we do not read your device
            location.
          </p>
        </Section>

        <Section id="security" title="Security">
          <p>
            We protect your data with industry-standard measures including encryption in transit (HTTPS/TLS),
            http-only and secure session cookies, restricted database access and server-side validation of requests.
            No method of transmission or storage is completely secure, but we work to protect your information and to
            respond promptly to any issue.
          </p>
        </Section>

        <Section id="international" title="International processing">
          <p>
            We and our service providers may process and store your information in countries other than your own,
            including the United States. Where required, we rely on appropriate safeguards for such transfers. By
            using the Service you understand that your information may be processed in these locations.
          </p>
        </Section>

        <Section id="retention" title="Data retention">
          <p>
            We keep your personal data for as long as your account is active. When you delete your account, we
            permanently remove your account profile, birth data, saved charts, reports, preferences and usage records.
          </p>
          <p>
            We retain purchase and payment records in anonymised form (with your email and personal metadata removed)
            for the period required by applicable legal, tax and accounting rules — typically up to 7 years. Encrypted
            operational backups may retain residual copies for a short period and are overwritten on our normal backup
            cycle (typically within 30 days).
          </p>
        </Section>

        <Section id="your-rights" title="Your rights and choices">
          <p>You can, at any time:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li><span className="text-white/75">Access or correct</span> your information from your profile, or by contacting us.</li>
            <li><span className="text-white/75">Withdraw consent</span> — unsubscribe from emails using the link in any email, or in your settings.</li>
            <li><span className="text-white/75">Delete your account</span> — permanently delete your account and personal data at any time from{" "}
              <Link to="/delete-account" className="text-amber-400/80 hover:text-amber-400 underline" data-testid="privacy-delete-link">dailynakshatra.com/delete-account</Link>{" "}
              or from Settings in the app.</li>
          </ul>
          <p>
            Depending on where you live, you may have additional rights under laws such as the GDPR or CCPA, including
            the right to object to or restrict processing and the right to lodge a complaint with your local data
            protection authority. Contact us to exercise these rights.
          </p>
        </Section>

        <Section id="subscriptions" title="Subscriptions and billing">
          <p>
            If you hold a paid subscription, deleting your account does <span className="text-white/80">not</span>{" "}
            automatically cancel billing. Please cancel your subscription separately through the payment provider or
            the app store where you subscribed. If you need help, contact us at{" "}
            <a href="mailto:info@dailynakshatra.com" className="text-amber-400/80 hover:text-amber-400 underline">info@dailynakshatra.com</a>.
          </p>
        </Section>

        <Section id="children" title="Children's privacy">
          <p>
            The Service is not directed to children under 16, and we do not knowingly collect personal information
            from children. If you believe a child has provided us with personal data, please contact us and we will
            delete it.
          </p>
        </Section>

        <Section id="changes" title="Changes to this policy">
          <p>
            We may update this policy from time to time. When we make material changes, we will update the "Last
            updated" date above and, where appropriate, notify you within the Service.
          </p>
        </Section>

        <Section id="contact" title="Contact us">
          <p>
            Daily Nakshatra<br />
            Email: <a href="mailto:info@dailynakshatra.com" className="text-amber-400/80 hover:text-amber-400 underline">info@dailynakshatra.com</a>
          </p>
        </Section>

        <div className="pt-6 border-t border-white/5 text-center">
          <Link to="/delete-account" className="text-white/40 hover:text-white/70 text-sm" data-testid="privacy-footer-delete-link">
            Delete your account →
          </Link>
        </div>
      </main>
    </div>
  );
}
